import {
  Action,
  MemoizedSelector,
  createFeatureSelector,
  createSelector
} from '@ngrx/store';
import * as fromMMD from './mmd/reducer';
import * as fromCalendar from './calendar/reducer';
import * as fromMarkets from './markets/reducer';
import * as fromUSTreasuries from './us-treasuries/reducer';
import {
  SET_DEAL_DATE,
  OPEN_DRAWER,
  CLOSE_DRAWER,
  TOGGLE_DRAWER,
  RESET_DATA
} from './actions';
import moment from 'moment';
import { PayloadAction } from '@ipreo/muni-shared';

export interface MarketDrawerReducerState {
  calendar: fromCalendar.CalendarState;
  markets: fromMarkets.MarketsState;
  mmdReads: fromMMD.MMDState;
  usTreasuries: fromUSTreasuries.USTreasuriesState;
  dealDate: Date;
  open: boolean;
  live: boolean;
}

export interface MarketDrawerRootReducerState {
  marketDrawer: MarketDrawerReducerState;
}

const dealDateDefaultState = undefined;

export function dealDateReducer(
  dealDate: Date = dealDateDefaultState,
  action: PayloadAction<Date>
) {
  switch (action.type) {
    case SET_DEAL_DATE:
      return shouldUseLiveData(action.payload)
        ? moment()
            .startOf('day')
            .toDate()
        : action.payload;
    case RESET_DATA:
      return undefined;
    default:
      return dealDate;
  }
}

const openDefaultState = false;

export function openCloseReducer(
  open: boolean = openDefaultState,
  action: Action
) {
  switch (action.type) {
    case OPEN_DRAWER:
      return true;
    case CLOSE_DRAWER:
      return false;
    case TOGGLE_DRAWER:
      return !open;
    case RESET_DATA:
      return false;
    default:
      return open;
  }
}

const liveDefaultState = false;

export function liveReducer(
  live: boolean = liveDefaultState,
  action: PayloadAction<Date>
) {
  switch (action.type) {
    case SET_DEAL_DATE:
      return shouldUseLiveData(action.payload);
    case RESET_DATA:
      return false;
    default:
      return live;
  }
}

function shouldUseLiveData(dealDate: Date) {
  const date = moment(dealDate);
  return !date.isBefore(moment().subtract(48, 'hours'));
}

export const reducerDef = {
  calendar: fromCalendar.calendarReducer,
  markets: fromMarkets.marketsReducer,
  usTreasuries: fromUSTreasuries.usTreasuriesReducer,
  mmdReads: fromMMD.mmdReadsReducer,
  dealDate: dealDateReducer,
  open: openCloseReducer,
  live: liveReducer
};

export const selectMarketDrawerState: MemoizedSelector<
  { marketDrawer: MarketDrawerReducerState },
  MarketDrawerReducerState
> = createFeatureSelector<
  { marketDrawer: MarketDrawerReducerState },
  MarketDrawerReducerState
>('marketDrawer');

export const getRootState = createSelector(
  selectMarketDrawerState,
  (state: MarketDrawerReducerState) => state || ({} as MarketDrawerReducerState)
);

export const getDealDate = createSelector(
  getRootState,
  state => state.dealDate
);

export const getOpenState = createSelector(
  getRootState,
  (state: MarketDrawerReducerState) => state.open
);

export const getLiveState = createSelector(
  getRootState,
  (state: MarketDrawerReducerState) => state.live
);

export const getCalendarState = createSelector(
  getRootState,
  (state: MarketDrawerReducerState) => state.calendar
);

export const getMarketsState = createSelector(
  getRootState,
  (state: MarketDrawerReducerState) => state.markets
);

export const getUSTreasuriesState = createSelector(
  getRootState,
  (state: MarketDrawerReducerState) => state.usTreasuries
);

export const getMMDState = createSelector(
  getRootState,
  (state: MarketDrawerReducerState) => state.mmdReads
);

export const getMarketsDataState = createSelector(
  getMarketsState,
  fromMarkets.getMarkets
);

export const getMarketsSelectedState = createSelector(
  getMarketsState,
  fromMarkets.getSelected
);

export const getMarketsLoadingState = createSelector(
  getMarketsState,
  fromMarkets.getLoading
);

export const getMMDDataState = createSelector(
  getMMDState,
  fromMMD.getMMDReads
);

export const getMMDUnseenState = createSelector(
  getMMDState,
  fromMMD.getMMDUnseen
);

export const getMMDLoadingState = createSelector(
  getMMDState,
  fromMMD.getMMDLoading
);

export const getUSTreasuriesDataState = createSelector(
  getUSTreasuriesState,
  fromUSTreasuries.getTreasuries
);

export const getUSTreasuriesSelectedState = createSelector(
  getUSTreasuriesState,
  fromUSTreasuries.getSelected
);

export const getUSTreasuriesLoadingState = createSelector(
  getUSTreasuriesState,
  fromUSTreasuries.getLoading
);
